import { fintracStrPersonContactFields } from 'app/modules/fintracStr/formConfig/definitions/common/personContact';
import { fintracStrPersonDetailsFields } from 'app/modules/fintracStr/formConfig/definitions/common/personDetails';
import { fintracStrRegistrationIncorporationFields } from 'app/modules/fintracStr/formConfig/definitions/common/registrationIncorporation';
import {
  FINTRAC_STR_DEFINITION_TYPE_CODE,
  FINTRAC_STR_STRUCTURE_TYPE_CODE,
  FINTRAC_STR_STRUCTURE_TYPE_CODE_OPTIONS,
  FINTRAC_STR_YES_NO_OPTIONS,
} from 'app/modules/fintracStr/formConstants';
import { getDependentPaths } from 'app/modules/fintracStr/formUtils';
import {
  FintracFormFieldConditional,
  FintracFormSingleField,
  FintracFormValue,
} from 'app/modules/fintracStr/models';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

const isEntityDetailsDefinition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_DETAILS.value ||
  value ===
    FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_AND_BENEFICIAL_OWNERSHIP_DETAILS
      .value;

const isEntityAndBeneficialOwnershipDetailsDefinition = (
  value: FintracFormValue,
) =>
  value ===
  FINTRAC_STR_DEFINITION_TYPE_CODE.ENTITY_AND_BENEFICIAL_OWNERSHIP_DETAILS
    .value;

const isEntityDetailsHideCondition: FintracFormFieldConditional = {
  or: [
    {
      path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
      conditions: (value) => !isEntityDetailsDefinition(value),
    },
  ],
};

const isEntityAndBeneficialOwnershipDetailsHideCondition: FintracFormFieldConditional =
  {
    or: [
      {
        path: (sectionIndex) => `definitions[${sectionIndex}].typeCode`,
        conditions: (value) =>
          !isEntityAndBeneficialOwnershipDetailsDefinition(value),
      },
    ],
  };

export const FINTRAC_STR_DEFINITIONS_ENTITIES_COMPLEX_FIELDS: FintracFormSingleField[] =
  [
    // Below are fields shared between Entity Details and Entity and Beneficial Ownership Details
    {
      type: U21FormFieldTypes.TEXT,
      name: 'natureOfPrincipalBusiness',
      label: 'Nature of principal business',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isEntityDetailsHideCondition,
    },

    {
      type: U21FormFieldTypes.RADIO_GROUP,
      name: 'registrationIncorporationIndicator',
      label: 'Is the entity registered or incorporated?',
      mustExistInPayload: false,
      mustHaveValueIfExist: true,
      defaultValue: false,
      fieldProps: {
        options: FINTRAC_STR_YES_NO_OPTIONS,
        horizontal: true,
      },
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isEntityDetailsHideCondition,
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Registration/Incorporation',
      name: 'registrationsIncorporations',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityDetailsHideCondition],
          [`definitions[${sectionIndex}].registrationIncorporationIndicator`],
          sectionIndex,
          fieldSectionIndex,
        ),
      fields: fintracStrRegistrationIncorporationFields(
        (sectionIndex, fieldSectionIndex) =>
          `definitions[${sectionIndex}].registrationsIncorporations[${fieldSectionIndex}]`,
        isEntityDetailsHideCondition,
      ),
      hideIfTrue: {
        or: [
          ...(isEntityDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].registrationIncorporationIndicator`,
            conditions: (value) => !value,
          },
        ],
      },
    },

    // The following fields only apply to Entity and Beneficial Ownership Details
    [
      {
        type: U21FormFieldTypes.SELECT,
        name: 'structureTypeCode',
        label: 'Structure type code',
        fieldProps: {
          options: FINTRAC_STR_STRUCTURE_TYPE_CODE_OPTIONS,
        },
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            [isEntityAndBeneficialOwnershipDetailsHideCondition],
            undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: isEntityAndBeneficialOwnershipDetailsHideCondition,
      },
      {
        type: U21FormFieldTypes.TEXT,
        name: 'structureTypeOther',
        label: 'Structure type other',
        mustExistInPayload: false,
        mustHaveValueIfExist: false,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            [isEntityAndBeneficialOwnershipDetailsHideCondition],
            [`definitions[${sectionIndex}].structureTypeCode`],
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
            {
              path: (sectionIndex) =>
                `definitions[${sectionIndex}].structureTypeCode`,
              conditions: (value) =>
                value !==
                FINTRAC_STR_STRUCTURE_TYPE_CODE
                  .ENTITY_OTHER_THAN_CORPORATION_OR_TRUST.value,
            },
          ],
        },
      },
    ],
    {
      type: 'section',
      repeatable: true,
      label: 'Directors of Corporation',
      name: 'directorsOfCorporation',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      fields: fintracStrPersonContactFields(
        (sectionIndex, fieldSectionIndex) =>
          `definitions[${sectionIndex}].directorsOfCorporation[${fieldSectionIndex}]`,
        isEntityAndBeneficialOwnershipDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityAndBeneficialOwnershipDetailsHideCondition],
          [`definitions[${sectionIndex}].structureTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        or: [
          ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].structureTypeCode`,
            conditions: (value) =>
              value !== FINTRAC_STR_STRUCTURE_TYPE_CODE.CORPORATION.value,
          },
        ],
      },
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Persons Owning Shares of Corporation',
      name: 'personsOwningSharesOfCorporation',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      fields: fintracStrPersonDetailsFields(
        isEntityAndBeneficialOwnershipDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityAndBeneficialOwnershipDetailsHideCondition],
          [`definitions[${sectionIndex}].structureTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        or: [
          ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].structureTypeCode`,
            conditions: (value) =>
              value !== FINTRAC_STR_STRUCTURE_TYPE_CODE.CORPORATION.value,
          },
        ],
      },
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Trustees of Trust',
      name: 'trusteesOfTrust',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      fields: fintracStrPersonContactFields(
        (sectionIndex, fieldSectionIndex) =>
          `definitions[${sectionIndex}].trusteesOfTrust[${fieldSectionIndex}]`,
        isEntityAndBeneficialOwnershipDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityAndBeneficialOwnershipDetailsHideCondition],
          [`definitions[${sectionIndex}].structureTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        or: [
          ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].structureTypeCode`,
            conditions: (value) =>
              value !== FINTRAC_STR_STRUCTURE_TYPE_CODE.TRUST.value &&
              value !==
                FINTRAC_STR_STRUCTURE_TYPE_CODE
                  .WIDELY_HELD_OR_PUBLICLY_TRADED_TRUST.value,
          },
        ],
      },
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Settlors of Trust',
      name: 'settlorsOfTrust',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      fields: fintracStrPersonContactFields(
        (sectionIndex, fieldSectionIndex) =>
          `definitions[${sectionIndex}].settlorsOfTrust[${fieldSectionIndex}]`,
        isEntityAndBeneficialOwnershipDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityAndBeneficialOwnershipDetailsHideCondition],
          [`definitions[${sectionIndex}].structureTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        or: [
          ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].structureTypeCode`,
            conditions: (value) =>
              value !== FINTRAC_STR_STRUCTURE_TYPE_CODE.TRUST.value &&
              value !==
                FINTRAC_STR_STRUCTURE_TYPE_CODE
                  .WIDELY_HELD_OR_PUBLICLY_TRADED_TRUST.value,
          },
        ],
      },
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Persons Owning Units of Trust',
      name: 'personsOwningUnitsOfTrust',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      fields: fintracStrPersonContactFields(
        (sectionIndex, fieldSectionIndex) =>
          `definitions[${sectionIndex}].personsOwningUnitsOfTrust[${fieldSectionIndex}]`,
        isEntityAndBeneficialOwnershipDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityAndBeneficialOwnershipDetailsHideCondition],
          [`definitions[${sectionIndex}].structureTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        or: [
          ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].structureTypeCode`,
            conditions: (value) =>
              value !==
              FINTRAC_STR_STRUCTURE_TYPE_CODE
                .WIDELY_HELD_OR_PUBLICLY_TRADED_TRUST.value,
          },
        ],
      },
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Beneficiaries of Trust',
      name: 'beneficiariesOfTrust',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      fields: fintracStrPersonContactFields(
        (sectionIndex, fieldSectionIndex) =>
          `definitions[${sectionIndex}].beneficiariesOfTrust[${fieldSectionIndex}]`,
        isEntityAndBeneficialOwnershipDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityAndBeneficialOwnershipDetailsHideCondition],
          [`definitions[${sectionIndex}].structureTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        or: [
          ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].structureTypeCode`,
            conditions: (value) =>
              value !== FINTRAC_STR_STRUCTURE_TYPE_CODE.TRUST.value,
          },
        ],
      },
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Persons Owning Entity Not Corporation or Trust',
      name: 'personsOwningEntityNotCorporationOrTrust',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      fields: fintracStrPersonDetailsFields(
        isEntityAndBeneficialOwnershipDetailsHideCondition,
      ),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityAndBeneficialOwnershipDetailsHideCondition],
          [`definitions[${sectionIndex}].structureTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        or: [
          ...(isEntityAndBeneficialOwnershipDetailsHideCondition.or ?? []),
          {
            path: (sectionIndex) =>
              `definitions[${sectionIndex}].structureTypeCode`,
            conditions: (value) =>
              value !==
              FINTRAC_STR_STRUCTURE_TYPE_CODE
                .ENTITY_OTHER_THAN_CORPORATION_OR_TRUST.value,
          },
        ],
      },
    },
    {
      type: 'section',
      repeatable: true,
      label: 'Authorized Persons',
      name: 'authorizedPersons',
      mustExistInPayload: true,
      mustHaveValueIfExist: false,
      maxRepeat: 3,
      defaultNumOfEntries: 0,
      fields: fintracStrPersonDetailsFields(isEntityDetailsHideCondition),
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          [isEntityDetailsHideCondition],
          undefined,
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: isEntityDetailsHideCondition,
    },
  ];
